import * as React from 'react';
import * as moment from 'moment';
import * as qs from 'query-string';
import * as Cookies from "universal-cookie";
import { Dispatch, connect } from 'react-redux';
import { IReportState, ReportActionCreators, CenterActionCreators, IApplicationState, AppActionCreators, IBasicCenterListResponse, IAppState } from '@nkr/store/index';
import { ISortBy, ICenterBase, ICenter } from '@nkr/models/index';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { IDashPerformance, IDashDonorData, IDashVolume } from '@nkr/models/index';
import { IDashSummaryDonorDataResponse, IDashSummaryPerformanceResponse, IDashSummaryVolumeResponse, IExpiredDonorDataResponse, IExpiredDonorRow } from '@nkr/store/reports';
import { DashDonorData, DashPerformance, DashSummaryVolume } from './constants';
import Filter from './filter';
import DonorProgress from './donor-progress';
import Cr1 from './cr1';
import ExpiredRuleOuts from './expired-rule-outs';
import Performance from './performance';
import DonorType from './donor-type';
import { OrganType } from '@nkr/enums/organ-type';

interface SummaryPayload {
    includeDash: boolean,
    includeNkr: boolean,
    includeUdash: boolean,
    includeMs: boolean,
    organType: number,
    centerId: number
}

interface IOwnProps {
    centerId: number,
    isAdmin: boolean
}

interface IStateProps {
    reports: IReportState
    centerList: ICenter[],
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getBasicCenterList: () => Promise<IBasicCenterListResponse>,
    getSummaryVolume: (includeNkr: boolean, includeDash: boolean, donationType: number, centerId: number, doGroupByCenter?: boolean, isYearly?: boolean, includeUdash?: boolean, includeMs?: boolean, isAdmin?: boolean) => Promise<IDashSummaryVolumeResponse>,
    getSummaryPerformance: (includeNkr: boolean, includeDash: boolean, startDate: string, endDate: string, donationType: number, centerId: number, doGroupByCenter?: boolean, includeUdash?: boolean, includeMs?: boolean, isAdmin?: boolean) => Promise<IDashSummaryPerformanceResponse>,
    getSummaryDonorData: (includeNkr: boolean, includeDash: boolean, startDate: string, endDate: string, donationType: number, centerId: number, doGroupByCenter?: boolean, includeUdash?: boolean, includeMs?: boolean, isAdmin?: boolean) => Promise<IDashSummaryDonorDataResponse>,
    getExpiredData: (includeNkr: boolean, includeDash: boolean, startDate: string, endDate: string, donationType: number, centerId: number, includeUdash?: boolean, includeMs?: boolean, isAdmin?: boolean) => Promise<IExpiredDonorDataResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    includeDash: boolean,
    includeNkr: boolean,
    includeUdash: boolean,
    includeMs: boolean,
    organType: number,
    sortBy: ISortBy[],
    centerList: ICenterBase[],
    performance: IDashPerformance,
    donorData: IDashDonorData,
    expiredData: IExpiredDonorRow[],
    summaryVolume: IDashVolume,
    centerId: number,
    startDate: string,
    endDate: string,
}

class DashboardSummary extends React.Component<TProps, IState> {
    readonly state: IState = {
        centerId: 0,
        includeDash: false,
        includeNkr: true,
        includeUdash: false,
        includeMs: false,
        organType: 3,
        sortBy: [],
        centerList: [],
        performance: DashPerformance,
        donorData: DashDonorData,
        expiredData: [] as IExpiredDonorRow[],
        summaryVolume: {
            centerId: null,
            centerAlias: moment().format("YYYY") + ' Total Contacts',
            year: '',
            janCount: 0,
            febCount: 0,
            marCount: 0,
            aprCount: 0,
            mayCount: 0,
            junCount: 0,
            julCount: 0,
            augCount: 0,
            sepCount: 0,
            octCount: 0,
            novCount: 0,
            decCount: 0,
            total: 0
        },
        startDate: moment().subtract(3, 'months').format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let newType = this.props.app.site.organType == OrganType.Kidney ? 3 : 2;
        let defaultSites = this.props.isAdmin ? "sites[]=ms&sites[]=nkr" : "sites[]=dash";
        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(defaultSites + "&organType=" + newType), { arrayFormat: 'bracket' });
        let cookies = new Cookies();

        let organTypeFromCookie = cookies.get('organType');

        if (organTypeFromCookie == null) {
            const current = new Date();
            const nextYear = new Date();

            nextYear.setFullYear(current.getFullYear() + 1);

            cookies.set("organType", newType, { path: "/", expires: nextYear, domain: __DOMAIN__, secure: __ISSECURE__, httpOnly: false });

            organTypeFromCookie = newType;
        } else {
            if (this.props.app.site.organType != OrganType.Kidney) {
                organTypeFromCookie = 2;
            }
        }

        let organTypeForPayload = organTypeFromCookie;

        if (parsed.organType != null) {
            organTypeForPayload = parsed.organType;
        }

        let payloadCenterId = this.props.centerId != null ? this.props.centerId : 0;

        if (this.props.isAdmin) {
            if (parsed.centerId != null) {
                payloadCenterId = parseInt(parsed.centerId);
            }
        }

        let summaryPayload: SummaryPayload = {
            includeDash: parsed.sites != null ? parsed.sites.indexOf('dash') >= 0 : false,
            includeNkr: parsed.sites != null ? parsed.sites.indexOf('nkr') >= 0 : true,
            includeUdash: parsed.sites != null ? parsed.sites.indexOf('udash') >= 0 : false,
            includeMs: parsed.sites != null ? parsed.sites.indexOf('ms') >= 0 : false,
            organType: organTypeForPayload,
            centerId: payloadCenterId
        }

        let { includeNkr, includeDash, includeUdash, includeMs, organType, centerId } = summaryPayload;
        let { startDate, endDate } = this.state;

        if (parsed.sd != null) {
            startDate = parsed.sd;
        }

        if (parsed.ed != null) {
            endDate = parsed.ed;
        }

        if (parsed.sites != null) {
            if (parsed.sites.indexOf('nkr') >= 0) {
                includeNkr = true;
            }

            if (parsed.sites.indexOf('dash') >= 0) {
                includeDash = true;
            }

            if (this.props.isAdmin) {
                if (parsed.sites.indexOf('udash') >= 0) {
                    includeUdash = true;
                }

                if (parsed.sites.indexOf('ms') >= 0) {
                    includeMs = true;
                }
            }
        }

        this.props.showLoading("Getting report data, one moment please...").then(() => {
            Promise.all([
                this.props.getBasicCenterList(),
                this.props.getSummaryVolume(includeNkr, includeDash, organType, centerId, false, true, includeUdash, includeMs, this.props.isAdmin),
                this.props.getSummaryPerformance(includeNkr, includeDash, startDate, endDate, organType, centerId, false, includeUdash, includeMs, this.props.isAdmin),
                this.props.getSummaryDonorData(includeNkr, includeDash, startDate, endDate, organType, centerId, false, includeUdash, includeMs, this.props.isAdmin),
                this.props.getExpiredData(includeNkr, includeDash, startDate, endDate, organType, centerId, includeUdash, includeMs, this.props.isAdmin)
            ])
                .then(([centerListResponse, summaryVolumeResponse, summaryPerformanceResponse, summaryDonorDataResponse, expiredDataResponse]) => {
                    let { results: centerList } = centerListResponse.data;

                    let { summaryVolume, performance, donorData } = this.state;
                    let { reportResults: summary } = summaryVolumeResponse.data.results;
                    let { reportResults: perf } = summaryPerformanceResponse.data.results;
                    let { reportResults: donor } = summaryDonorDataResponse.data.results;
                    let expiredData = expiredDataResponse.data.results.reportResults || [];

                    if (summary.length > 0) {
                        summaryVolume = summary[0];
                    } else {
                        let dashSum = DashSummaryVolume;
                        dashSum.year = moment().format("YYYY") + ' Total Contacts';
                        summaryVolume = DashSummaryVolume;
                    }

                    if (perf.length > 0) {
                        performance = perf[0];
                    } else {
                        performance = DashPerformance;
                    }

                    if (donor.length > 0) {
                        donorData = donor[0];
                    } else {
                        donorData = DashDonorData;
                    }

                    this.setState({
                        centerList,
                        summaryVolume,
                        performance,
                        donorData,
                        expiredData,
                        organType,
                        includeDash,
                        includeNkr,
                        includeUdash,
                        includeMs,
                        centerId,
                        startDate,
                        endDate
                    }, () => {
                        this.setConversionUrl();

                        this.props.hideLoading();
                    });
                }).catch(() => {
                    // alert('An error occured while loading report data');
                    this.props.hideLoading();
                });
        });
    }

    handleIncludeChange = (property: string) => {
        if (property == 'includeNkr') {
            this.setState({ includeNkr: !this.state.includeNkr });
        }

        if (property == 'includeDash') {
            this.setState({ includeDash: !this.state.includeDash });
        }

        if (property == 'includeUdash') {
            this.setState({ includeUdash: !this.state.includeUdash });
        }

        if (property == 'includeMs') {
            this.setState({ includeMs: !this.state.includeMs });
        }
    }

    handleFilter = () => {
        let { includeNkr, includeDash, includeUdash, includeMs, organType, centerId } = this.state;
        let { startDate, endDate } = this.state;

        if (!this.props.isAdmin) {
            includeUdash = false;
            includeMs = false;
        }

        if (!includeNkr && !includeDash && !includeUdash && !includeMs) {
            includeNkr = true;
        }

        if (this.props.isAdmin == false) {
            centerId = this.props.centerId;
        }

        this.props.showLoading("Getting report data, one moment please...").then(() => {
            Promise.all([
                this.props.getSummaryVolume(includeNkr, includeDash, organType, centerId, false, true, includeUdash, includeMs, this.props.isAdmin),
                this.props.getSummaryPerformance(includeNkr, includeDash, startDate, endDate, organType, centerId, false, includeUdash, includeMs, this.props.isAdmin),
                this.props.getSummaryDonorData(includeNkr, includeDash, startDate, endDate, organType, centerId, false, includeUdash, includeMs, this.props.isAdmin),
                this.props.getExpiredData(includeNkr, includeDash, startDate, endDate, organType, centerId, includeUdash, includeMs, this.props.isAdmin)
            ])
                .then(([summaryVolumeResponse, summaryPerformanceResponse, summaryDonorDataResponse, expiredDataResponse]) => {
                    let { summaryVolume, performance, donorData } = this.state;
                    let { reportResults: summary } = summaryVolumeResponse.data.results;
                    let { reportResults: perf } = summaryPerformanceResponse.data.results;
                    let { reportResults: donor } = summaryDonorDataResponse.data.results;
                    let expiredData = expiredDataResponse.data.results.reportResults || [];

                    if (summary.length > 0) {
                        summaryVolume = summary[0];
                    } else {
                        let dashSum = DashSummaryVolume;
                        dashSum.year = moment().format("YYYY") + ' Total Contacts';
                        summaryVolume = DashSummaryVolume;
                    }

                    if (perf.length > 0) {
                        performance = perf[0];
                    } else {
                        performance = DashPerformance;
                    }

                    if (donor.length > 0) {
                        donorData = donor[0];
                    } else {
                        donorData = DashDonorData;
                    }

                    this.setState({
                        summaryVolume,
                        performance,
                        donorData,
                        expiredData,
                        organType,
                        includeDash,
                        includeNkr,
                        includeUdash,
                        includeMs,
                    }, () => {
                        this.setConversionUrl();
                        this.props.hideLoading();
                    });
                }).catch(() => {
                    // alert('An error occured while loading report data');
                    this.props.hideLoading();
                })
        });
    }

    handleOrganFilter = (organType: number) => {
        this.setState({
            organType
        }, () => {
            this.handleFilter()
        });
    }

    handleFilterChange = (startDate: string, endDate: string, centerId: number) => {
        this.setState({
            startDate,
            endDate,
            centerId
        });
    }

    setConversionUrl = () => {
        let { includeDash, includeNkr, includeUdash, includeMs, organType } = this.state;

        let paramList = [];

        if (includeDash) {
            paramList.push(encodeURI('sites[]=dash'));
        }

        if (includeNkr) {
            paramList.push(encodeURI('sites[]=nkr'));
        }

        if (includeUdash) {
            paramList.push(encodeURI('sites[]=udash'));
        }

        if (includeMs) {
            paramList.push(encodeURI('sites[]=ms'));
        }

        if (organType != null) {
            paramList.push(encodeURI('organType=' + organType));
        }

        if (this.props.isAdmin) {
            paramList.push('centerId=' + this.state.centerId);
        }

        paramList.push('sd=' + this.state.startDate);
        paramList.push('ed=' + this.state.endDate);

        let params = paramList.join('&');

        let newUrl = '/center/' + this.props.centerId + '/reports/dash-board/summary?' + params;

        if (this.props.isAdmin) {
            newUrl = '/admin/reports/dash-board/summary?' + params;
        }

        window.history.pushState({}, '', newUrl);
    }

    getOrganTypeTitle = () => {
        if (this.state.organType == 1) {
            return 'Kidney Only';
        }

        if (this.state.organType == 2) {
            return 'Liver Only';
        }

        if (this.state.organType == 3) {
            return 'Liver & Kidney';
        }
    }

    formatDate = (date: string) => {
        var d = moment(date).format('MM/DD/YYYY');
        return d
    }

    getCurrentYear = () => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        return currentYear.toString();
    }

    render = () => {
        const organTypeTitle = this.getOrganTypeTitle();

        let { startDate, endDate, performance, donorData, expiredData, summaryVolume } = this.state;

        let formattedStartDate = this.formatDate(startDate);
        let formattedEndDate = this.formatDate(endDate);

        return (
            <>
                <div style={{ marginBottom: 10, padding: '0px 5px 5px 5px' }}>
                    <div style={{ display: 'flex', justifyContent: 'end', marginBottom: 5 }}>
                        <span>
                            <div>View:&nbsp;
                                <DropdownButton bsStyle='primary' disabled={this.props.app.site.organType == OrganType.Liver} pullRight title={organTypeTitle} id='organType'>
                                    <MenuItem onClick={() => this.handleOrganFilter(2)}>Liver Candidates</MenuItem>
                                    <MenuItem onClick={() => this.handleOrganFilter(1)}>Kidney Candidates</MenuItem>
                                    <MenuItem onClick={() => this.handleOrganFilter(3)}>Liver &amp; Kidney Candidates</MenuItem>
                                </DropdownButton>
                            </div>
                        </span>
                    </div>

                    <Filter
                        isAdmin={this.props.isAdmin}
                        centerId={this.state.centerId}
                        includeDash={this.state.includeDash}
                        includeNkr={this.state.includeNkr}
                        includeUdash={this.state.includeUdash}
                        includeMs={this.state.includeMs}
                        startDate={this.state.startDate}
                        endDate={this.state.endDate}
                        centerList={this.state.centerList}
                        onChange={this.handleIncludeChange}
                        onFilterChange={this.handleFilterChange}
                        onFilter={this.handleFilter} />

                    {/* AS -> To be hidden for now while David Spec's new enhancement
                    <div className="row">
                        <div className='col-xs-12'>
                            <Volume data={summaryVolume} />
                        </div>
                    </div> */}

                    <div className="row">
                        <div className='col-xs-12 col-sm-6'>
                            <DonorProgress
                                data={donorData}
                                formattedStartDate={formattedStartDate}
                                formattedEndDate={formattedEndDate}
                                isAdmin={this.props.isAdmin} />
                            {!(this.state.includeDash && !this.state.includeNkr && !this.state.includeUdash && !this.state.includeMs) && (
                                <Cr1
                                    data={donorData}
                                    formattedStartDate={formattedStartDate}
                                    formattedEndDate={formattedEndDate} />
                            )}
                        </div>
                        {this.props.isAdmin && (
                            <div className='col-xs-12 col-sm-6'>
                                <ExpiredRuleOuts
                                    data={expiredData}
                                    formattedStartDate={formattedStartDate}
                                    formattedEndDate={formattedEndDate} />
                            </div>
                        )}
                        {!this.props.isAdmin && (
                            <>
                                <div className='col-xs-12 col-sm-6'>
                                    <Performance
                                        data={performance}
                                        formattedStartDate={formattedStartDate}
                                        formattedEndDate={formattedEndDate} />
                                </div>
                                <div className='col-xs-12 col-sm-6'>
                                    <DonorType
                                        data={donorData}
                                        formattedStartDate={formattedStartDate}
                                        formattedEndDate={formattedEndDate} />
                                    <div>
                                        * The total of donors identified as NDD/DD/SV/FV may not equal 100% because some donors overlap categories or remain unclassified.
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    };
}

const mapStateToProps = (appState: IApplicationState, ownProps: IOwnProps): IStateProps => {
    return {
        reports: appState.reports,
        centerList: appState.centers.list,
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getBasicCenterList: () => {
            return dispatch(CenterActionCreators.getBasicCenterList(false));
        },
        getSummaryVolume: (includeNkr: boolean, includeDash: boolean, donationType: number, centerId: number, doGroupByCenter: boolean = false, isYearly: boolean = true, includeUdash: boolean = false, includeMs: boolean = false, isAdmin: boolean = false) => {
            return dispatch(ReportActionCreators.getSummaryVolume(includeNkr, includeDash, donationType, centerId, doGroupByCenter, isYearly, includeUdash, includeMs, isAdmin));
        },
        getSummaryPerformance: (includeNkr: boolean, includeDash: boolean, startDate: string, endDate: string, donationType: number, centerId: number, doGroupByCenter: boolean = false, includeUdash: boolean = false, includeMs: boolean = false, isAdmin: boolean = false) => {
            return dispatch(ReportActionCreators.getSummaryPerformance(includeNkr, includeDash, startDate, endDate, donationType, centerId, doGroupByCenter, includeUdash, includeMs, isAdmin));
        },
        getSummaryDonorData: (includeNkr: boolean, includeDash: boolean, startDate: string, endDate: string, donationType: number, centerId: number, doGroupByCenter: boolean = false, includeUdash: boolean = false, includeMs: boolean = false, isAdmin: boolean = false) => {
            return dispatch(ReportActionCreators.getSummaryDonorData(includeNkr, includeDash, startDate, endDate, donationType, centerId, doGroupByCenter, includeUdash, includeMs, isAdmin));
        },
        getExpiredData: (includeNkr: boolean, includeDash: boolean, startDate: string, endDate: string, donationType: number, centerId: number, includeUdash: boolean = false, includeMs: boolean = false, isAdmin: boolean = false) => {
            return dispatch(ReportActionCreators.getExpiredData(includeNkr, includeDash, startDate, endDate, donationType, centerId, includeUdash, includeMs, isAdmin));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(DashboardSummary);